import { useState, useCallback, useMemo } from 'react';

import { fetchMappingResultsFromS3 } from '../api/mappingApi';
import { fetchMockMappingResults } from '../mockData';
import { MappingResponse, MappingDialogStep } from '../types';

/**
 * INTEGRATION STATUS SUMMARY
 * =========================
 *
 * This hook manages the Mapping Review Dialog state and logic.
 *
 * CURRENT STATE: Real API Integration with Mock Fallback
 * - Frontend: Fully functional UI components
 * - Backend: API + OMOP mapper ready
 * - Integration: API CALLS CONNECTED
 *
 * WHAT'S READY (Backend):
 * - OMOP Mapping Agent exists with tools to map codes
 * - OMOPCodeMapper class performs actual mapping (Snowflake)
 * - MappingResult models match frontend types
 * - transform_to_mapping_results() transforms output for UI
 * - Message Gateway can stream events to frontend
 * - Message Gateway S3 endpoint: GET /codes/{session_id}/{file_name}
 *
 * INTEGRATION COMPLETE:
 * - Backend saves results to S3 after mapping
 * - Frontend receives METADATA events with omop_session_info containing session_id and file_name
 * - Frontend retrieves results from S3 via message-gateway endpoint
 * - API response transformed to MappingResponse format
 * - Error handling with user-friendly messages
 */

export interface UseMappingReviewDialogReturn {
  /** Whether the dialog is currently open */
  isDialogOpen: boolean;

  /** Current step in the dialog workflow */
  currentStep: MappingDialogStep;

  /** Mapping results data (from API or mock) */
  mappingData: MappingResponse | null;

  /** Whether data is currently loading */
  isLoading: boolean;

  /** Error message if API call failed */
  error: string | null;

  /** Whether the dialog is valid for submission */
  isValidForSubmit: boolean;

  /** Whether the download filename dialog is open */
  showDownloadDialog: boolean;

  /** Opens the dialog and loads mapping results with optional session identifiers */
  handleOpenDialog: (sessionId?: string, fileName?: string) => Promise<void>;

  /** Closes the dialog and resets state */
  handleCloseDialog: () => void;

  /** Moves from review step to confirm step */
  handleProceedToConfirm: () => void;

  /** Goes back from confirm step to review step */
  handleBackToReview: () => void;

  /** Final submission handler - opens download dialog */
  handleConfirmMapping: () => void;

  /** Cancels the download dialog */
  handleCancelDownload: () => void;
}

export const useMappingReviewDialog = (): UseMappingReviewDialogReturn => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<MappingDialogStep>('review');
  const [mappingData, setMappingData] = useState<MappingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);

  // ============================================================================
  // COMPUTED STATE
  // ============================================================================

  /**
   * Dialog is valid for submission if we have data and we're past the loading state
   */
  const isValidForSubmit = useMemo(() => {
    return Boolean(mappingData && !isLoading && !error);
  }, [mappingData, isLoading, error]);

  // ============================================================================
  // DIALOG LIFECYCLE HANDLERS
  // ============================================================================

  /**
   * Opens the dialog and fetches mapping results
   *
   * @param sessionId - Session ID from OMOP mapping agent (optional for real API)
   * @param fileName - File name from mapping session (optional for real API)
   *
   * If both sessionId and fileName are provided, fetches from message-gateway API.
   * If neither provided, falls back to mock data for development/testing.
   */
  const handleOpenDialog = useCallback(async (sessionId?: string, fileName?: string) => {
    setIsDialogOpen(true);
    setCurrentStep('review');
    setIsLoading(true);
    setError(null);

    try {
      if (sessionId && fileName) {
        // Real API call with session identifiers
        // Get original filename from localStorage if available
        const results = await fetchMappingResultsFromS3(sessionId, fileName);
        setMappingData(results);
      } else {
        // Fallback to mock data for development
        console.warn('No session data provided, falling back to MOCK data');
        const mockResults = await fetchMockMappingResults();
        setMappingData(mockResults);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Error in handleOpenDialog:', {
        error: errorMessage,
        sessionId,
        fileName,
        stack: err instanceof Error ? err.stack : 'no stack',
      });
      setError(errorMessage);
      setMappingData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Closes the dialog and resets all state
   */
  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setCurrentStep('review');
    setMappingData(null);
    setIsLoading(false);
    setError(null);
    setShowDownloadDialog(false);
  }, []);

  // ============================================================================
  // WORKFLOW NAVIGATION HANDLERS
  // ============================================================================

  /**
   * Moves from 'review' step to 'confirm' step
   */
  const handleProceedToConfirm = useCallback(() => {
    setCurrentStep('confirm');
  }, []);

  /**
   * Goes back from 'confirm' step to 'review' step
   */
  const handleBackToReview = useCallback(() => {
    setCurrentStep('review');
  }, []);

  // ============================================================================
  // SUBMISSION HANDLER
  // ============================================================================

  /**
   * Final submission handler - called when user confirms the mappings
   * Opens the download dialog to prompt for filename
   */
  const handleConfirmMapping = useCallback(() => {
    setShowDownloadDialog(true);
  }, []);

  /**
   * Handles the actual download with the user-provided filename
   */

  /**
   * Cancels the download dialog
   */
  const handleCancelDownload = useCallback(() => {
    setShowDownloadDialog(false);
  }, []);

  // ============================================================================
  // RETURN HOOK INTERFACE
  // ============================================================================

  return {
    isDialogOpen,
    currentStep,
    mappingData,
    isLoading,
    error,
    isValidForSubmit,
    showDownloadDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleProceedToConfirm,
    handleBackToReview,
    handleConfirmMapping,
    handleCancelDownload,
  };
};
